/**
 * Paragraphs persistence — GraphQL backed.
 *
 * Components use Apollo's `useQuery` / `useMutation` directly. This module
 * exposes only the localStorage migration helper (one-shot import on first
 * visit) and the storage key, so the rest of the app stays declarative.
 */

import { CREATE_PARAGRAPH, SEED_SAMPLE_PARAGRAPHS } from '../../graphql/paragraph-mutations';
import { GET_PARAGRAPHS } from '../../graphql/paragraph-queries';

export const LEGACY_STORAGE_KEY = 'inkwell_paragraphs_v1';

function readLegacyParagraphs() {
  try {
    const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
  } catch {
    return null;
  }
}

function clearLegacyParagraphs() {
  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * One-shot migration. If the backend list is empty:
 *   1. If localStorage has paragraphs → POST each via createParagraph,
 *      then clear localStorage.
 *   2. Otherwise → call seedSampleParagraphs to give the teacher a
 *      starter set.
 *
 * Returns the resulting list (refetched).
 *
 * @param {ApolloClient} client - Apollo client instance.
 * @param {Array} backendList - Result of GET_PARAGRAPHS.
 * @returns {Promise<Array>} - Latest paragraph list.
 */
export async function ensureParagraphsSeed(client, backendList) {
  if (backendList && backendList.length > 0) return backendList;

  const legacy = readLegacyParagraphs();
  if (legacy) {
    // Migrate localStorage paragraphs to backend.
    for (const p of legacy) {
      const input = {
        title: p.title || 'Untitled',
        subtitle: p.subtitle || '',
        hskLevel: typeof p.hsk === 'number' ? p.hsk : (p.hskLevel || 3),
        timeMinutes: p.timeMinutes || 3,
        sentences: (p.sentences || [])
          .filter((s) => (s.zh || '').trim())
          .map((s) => ({
            zh: s.zh,
            py: s.py || '',
            en: s.en || '',
            hint: s.hint || '',
            audio: s.audio || '',
          })),
        referenceTranslation: p.referenceTranslation || '',
      };
      if (input.sentences.length === 0) continue;
      try {
        await client.mutate({ mutation: CREATE_PARAGRAPH, variables: { input } });
      } catch (err) {
        console.warn('Failed to migrate paragraph', p.title, err);
      }
    }
    clearLegacyParagraphs();
  } else {
    // Seed defaults server-side.
    try {
      await client.mutate({ mutation: SEED_SAMPLE_PARAGRAPHS });
    } catch (err) {
      console.warn('Failed to seed sample paragraphs', err);
    }
  }

  const refreshed = await client.query({ query: GET_PARAGRAPHS, fetchPolicy: 'network-only' });
  return refreshed.data?.paragraphs || [];
}
